import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const DEFAULT_TIMEOUT_MS = 4000;

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /** A single optional follow-up, such as Undo. Pressing it clears the toast. */
  action?: ToastAction;
  /** Overrides the default lifetime, e.g. to leave an Undo up long enough to reach. */
  timeoutMs?: number;
}

export interface ToastState {
  message: string;
  action: ToastAction | null;
}

export type ShowToast = (message: string, options?: ToastOptions) => void;

export interface ToastController {
  toast: ToastState | null;
  show: ShowToast;
  dismiss: () => void;
}

/**
 * Transient confirmation for an action that has already taken effect.
 *
 * For feedback only — never for anything the user has to read or act on, which belongs in
 * a banner or a dialog instead. An action on the toast is a convenience, like Undo, that
 * the user is free to ignore.
 */
export function useToast(timeoutMs: number = DEFAULT_TIMEOUT_MS): ToastController {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timer.current != null) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => clear, [clear]);

  const show = useCallback<ShowToast>(
    (message, options = {}) => {
      // A second message restarts the clock rather than inheriting the first one's.
      clear();
      setToast({ message, action: options.action ?? null });
      timer.current = setTimeout(() => setToast(null), options.timeoutMs ?? timeoutMs);
    },
    [clear, timeoutMs],
  );

  const dismiss = useCallback(() => {
    clear();
    setToast(null);
  }, [clear]);

  return { toast, show, dismiss };
}

/**
 * The live region stays mounted whether or not there is a message.
 *
 * Screen readers only announce content inserted into a region that was already there, so
 * mounting the region together with its first message would announce nothing.
 */
export function Toast({ toast, onDismiss }: { toast: ToastState | null; onDismiss: () => void }) {
  return (
    <div className="toast-region" role="status" aria-live="polite">
      {toast != null &&
        (toast.action == null ? (
          <p className="toast">{toast.message}</p>
        ) : (
          <div className="toast toast-with-action">
            <span>{toast.message}</span>
            <button
              type="button"
              className="toast-action"
              onClick={() => {
                onDismiss();
                toast.action!.onClick();
              }}
            >
              {toast.action.label}
            </button>
            <button
              type="button"
              className="toast-dismiss"
              aria-label="Dismiss"
              onClick={onDismiss}
            >
              ×
            </button>
          </div>
        ))}
    </div>
  );
}

const ToastContext = createContext<ShowToast | null>(null);

/**
 * One toast region for the whole shell, mounted above the routed page.
 *
 * An action that ends by sending the user somewhere else — saving a shared link, say —
 * would otherwise unmount its own confirmation before anyone could read it.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { toast, show, dismiss } = useToast();
  return (
    <ToastContext.Provider value={show}>
      {children}
      <Toast toast={toast} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/** Only the setter is shared, so it stays referentially stable and effects may depend on it. */
export function useShowToast(): ShowToast {
  const show = useContext(ToastContext);
  if (show == null) throw new Error('useShowToast needs a ToastProvider above it');
  return show;
}
